import { html, css } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import moment from 'moment-timezone';

import { postDocument } from '../../../../packages/neb-api-client/src/document-api-client';
import { fetchMany as getCases } from '../../../../packages/neb-api-client/src/patient-cases';
import { getActiveProviderUsers } from '../../../../packages/neb-api-client/src/practice-users-api-client';
import {
  getProgressionOfCare,
  getProgressionOfCareHeader,
  printProgressionOfCareSummary,
  generateProgressionOfCare,
} from '../../../../packages/neb-api-client/src/progression-of-care-api-client';
import {
  openError,
  openSuccess,
} from '../../../../packages/neb-dialog/neb-banner-state';
import Overlay from '../../../../packages/neb-lit-components/src/components/overlays/neb-overlay';
import '../../../../packages/neb-lit-components/src/components/neb-button';
import { store } from '../../../../packages/neb-redux/neb-redux-store';
import {
  OVERLAY_WIDTH_MEDIUM,
  CSS_SPACING,
} from '../../../../packages/neb-styles/neb-variables';
import { parseDate } from '../../../../packages/neb-utils/date-util';
import {
  FEATURE_FLAGS,
  hasFeatureOrBeta,
} from '../../../../packages/neb-utils/feature-util';
import { printPdf } from '../../../../packages/neb-utils/neb-pdf-print-util';
import { formatNarrative } from '../../../formatters/ai-narrative';
import { formatHeaderInfo } from '../../../formatters/progression-of-care';
import { userpilotTrackEvent } from '../../../library/userpilot/track-events';
import {
  CSS_BANNER_INFO_BACKGROUND_COLOR,
  CSS_BANNER_INFO_BORDER_COLOR,
  CSS_COLOR_HIGHLIGHT,
  CSS_FONT_WEIGHT_BOLD,
  CSS_SMALL_SPACING,
} from '../../../styles';
import '../../filters/neb-filters-progression-of-care';
import '../../misc/neb-progression-of-care-header';
import '../../misc/neb-icon';
import '../../misc/neb-collapsible-card';
import '../../misc/neb-skeleton';
import '../../../../packages/neb-lit-components/src/components/neb-text';

export const ELEMENTS = {
  patientHeader: { id: 'patient-header' },
  generateSummaryButton: { id: 'generate-summary-button' },
  printButton: { id: 'print-button' },
  saveButton: { id: 'save-button' },
  title: { id: 'title' },
  copyButton: { id: 'copy-button' },
  copySuccess: { id: 'copy-success' },
  saveSuccess: { id: 'save-success' },
  primaryConcerns: { id: 'primary-concerns' },
  progressionOfCare: { id: 'progression-of-care' },
  providerNotes: { id: 'provider-notes' },
  headerSkeleton: { id: 'header-skeleton' },
  filters: { id: 'filters' },
  noEncountersMessage: { id: 'no-encounters-message' },
};

export const SECTION_LABELS = {
  PRIMARY_CONCERNS: 'Primary Concerns',
  PROGRESSION_OF_CARE: 'Progression of Care',
  PROVIDER_NOTES: 'Provider Notes',
};

const CHART_NOTE_SUMMARY_TITLE = 'Chart Note Summary';
const ERROR_MESSAGE = 'Error fetching progression of care';
const ERROR_MESSAGE_HEADER = 'Error fetching header information';
const ERROR_MESSAGE_FILTER_DATA = 'Error fetching filter data';

class NebOverlayProgressionOfCare extends Overlay {
  static get properties() {
    return {
      model: {
        type: Object,
        reflect: true,
      },
      layout: {
        type: String,
        reflect: true,
      },
      showCards: {
        type: Boolean,
        reflect: true,
      },
      copySuccess: {
        type: Boolean,
        reflect: true,
      },
      saveSuccess: {
        type: Boolean,
        reflect: true,
      },
      saveLoading: {
        type: Boolean,
        reflect: true,
      },
      sectionVisibility: {
        type: Object,
        reflect: true,
      },
      showSectionVisibility: {
        type: Boolean,
        reflect: true,
      },
      headerLoading: {
        type: Boolean,
        reflect: true,
      },
      primaryConcernsLoading: {
        type: Boolean,
        reflect: true,
      },
      progressionOfCareLoading: {
        type: Boolean,
        reflect: true,
      },
      providerNotesLoading: {
        type: Boolean,
        reflect: true,
      },
      primaryConcerns: {
        type: String,
        reflect: true,
      },
      progressionOfCare: {
        type: String,
        reflect: true,
      },
      providerNotes: {
        type: String,
        reflect: true,
      },
      headerInfo: {
        type: Object,
        reflect: true,
      },
      formattedHeaderInfo: {
        type: Object,
        reflect: true,
      },
      filterItems: {
        type: Object,
        reflect: true,
      },
      appliedFilters: {
        type: Object,
        reflect: true,
      },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        .content {
          width: ${OVERLAY_WIDTH_MEDIUM};
          padding: 0 10px;
          overflow: auto;
        }

        .container-button-summary {
          display: flex;
          justify-content: center;
          margin-top: ${CSS_SPACING};
        }

        .container-button-print {
          display: flex;
          justify-content: center;
          margin-top: ${CSS_SPACING};
        }

        .container-buttons {
          display: flex;
          justify-content: center;
          align-items: flex-start;
          gap: ${CSS_SMALL_SPACING};
          margin: ${CSS_SPACING} 0;
        }

        .container-title {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: ${CSS_SMALL_SPACING};
          margin: ${CSS_SMALL_SPACING} 0 ${CSS_SMALL_SPACING} 0;
        }

        .container-cards {
          display: flex;
          flex-direction: column;
          gap: ${CSS_SPACING};
          margin: 0 ${CSS_SPACING};
        }

        .icon {
          width: 20px;
          height: 20px;
        }

        .title {
          font-size: 18px;
          font-weight: ${CSS_FONT_WEIGHT_BOLD};
        }

        .container-button-summary neb-button.copy-button {
          font-size: 18px;
          font-weight: bold;
          margin-top: ${CSS_SPACING};
        }

        .copy-button-wrapper {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .copy-success {
          margin-top: ${CSS_SMALL_SPACING};
          color: #4caf50;
          font-size: 14px;
          opacity: 0;
          transition: opacity 0.3s ease;
          white-space: nowrap;
        }

        .copy-success.visible {
          opacity: 1;
        }

        .save-button-wrapper {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .save-success {
          margin-top: ${CSS_SMALL_SPACING};
          color: #4caf50;
          font-size: 14px;
          opacity: 0;
          transition: opacity 0.3s ease;
          white-space: nowrap;
        }

        .save-success.visible {
          opacity: 1;
        }

        .container-summary-note {
          display: flex;
          align-items: center;
          background-color: ${CSS_BANNER_INFO_BACKGROUND_COLOR};
          border: 1px solid ${CSS_BANNER_INFO_BORDER_COLOR};
          color: ${CSS_COLOR_HIGHLIGHT};
          padding: ${CSS_SMALL_SPACING};
          margin: 0 ${CSS_SPACING} ${CSS_SPACING};
          border-radius: 5px;
          gap: 5px;
        }

        .icon-summary-note {
          width: 16px;
          height: 16px;
          fill: #0caadc;
        }

        .skeleton-loading {
          margin: ${CSS_SPACING};
        }

        .no-encounters-message-container {
          display: flex;
          justify-content: center;
          align-items: center;
        }
      `,
    ];
  }

  constructor() {
    super();
    this.initState();
    this.initHandlers();
  }

  initState() {
    super.initState();

    this.headerInfo = {};
    this.formattedHeaderInfo = {};
    this.showCards = false;
    this.copySuccess = false;
    this.saveLoading = false;
    this.saveSuccess = false;
    this.showSectionVisibility = true;
    this.sectionVisibility = {
      [SECTION_LABELS.PRIMARY_CONCERNS]: true,
      [SECTION_LABELS.PROGRESSION_OF_CARE]: true,
      [SECTION_LABELS.PROVIDER_NOTES]: true,
    };

    this.model = { patientId: '' };

    this.headerLoading = false;
    this.primaryConcernsLoading = false;
    this.progressionOfCareLoading = false;
    this.providerNotesLoading = false;

    this.primaryConcerns = '';
    this.progressionOfCare = '';
    this.providerNotes = '';

    this.filterItems = {
      providers: [],
      cases: [],
    };

    this.appliedFilters = {
      providers: [],
      cases: [],
      dosFrom: null,
      dosTo: null,
    };

    this.__abortController = null;
    this.__showSummaryNote = false;
    this.__hasProgressionOfCareFilter = false;
  }

  initHandlers() {
    super.initHandlers();
    this.handlers = {
      ...this.handlers,
      generateSummary: async () => {
        this.showCards = true;

        this.sectionVisibility = {
          [SECTION_LABELS.PRIMARY_CONCERNS]: true,
          [SECTION_LABELS.PROGRESSION_OF_CARE]: true,
          [SECTION_LABELS.PROVIDER_NOTES]: true,
        };

        await this.__generateProgressionOfCare();
      },
      copyContent: () => {
        this.__copyContentToClipboard();
      },
      printSummary: async () => {
        try {
          const printPromise = this.__generatePdfBuffer();
          await printPdf(printPromise);
        } catch (error) {
          console.error('Error printing progression of care summary:', error);
        }
      },
      saveSummary: async () => {
        try {
          this.saveLoading = true;
          const pdfBuffer = await this.__generatePdfBuffer();
          const { success } = await this.__savePdfToPatientDocuments(pdfBuffer);

          if (success) {
            this.saveSuccess = true;
            setTimeout(() => {
              this.saveSuccess = false;
            }, 2000);
          }
        } catch (error) {
          store.dispatch(openError('Error saving progression of care summary'));
        } finally {
          this.saveLoading = false;
        }
      },
      togglePrimaryConcerns: () => {
        this.__toggleSectionVisibility(SECTION_LABELS.PRIMARY_CONCERNS);
      },
      toggleProgressionOfCare: () => {
        this.__toggleSectionVisibility(SECTION_LABELS.PROGRESSION_OF_CARE);
      },
      toggleProviderNotes: () => {
        this.__toggleSectionVisibility(SECTION_LABELS.PROVIDER_NOTES);
      },
      applyFilters: filters => {
        this.appliedFilters = filters;

        this.showCards = true;
        this.__fetchHeaderInformation();
        this.__generateProgressionOfCare();
      },
      resetFilters: () => {
        this.appliedFilters = {
          providers: [],
          cases: [],
          dosFrom: null,
          dosTo: null,
        };

        const { providers } = this.filterItems;
        const { cases } = this.filterItems;

        this.filterItems = {
          providers: providers.map(provider => ({
            ...provider,
            checked: false,
          })),
          cases: cases.map(caseItem => ({
            ...caseItem,
            checked: false,
          })),
        };
      },
      changeFilters: filters => {
        this.appliedFilters = filters;
      },
    };
  }

  async connectedCallback() {
    super.connectedCallback();

    this.__hasProgressionOfCareFilter = await hasFeatureOrBeta(
      FEATURE_FLAGS.EGG_PROGRESSION_OF_CARE_FILTER,
    );

    const promises = [this.__fetchHeaderInformation()];

    if (this.__hasProgressionOfCareFilter) {
      promises.push(this.__fetchFilterData());
    }

    await Promise.all(promises);
  }

  disconnectedCallback() {
    if (this.__abortController) {
      this.__abortController.abort();
    }
    super.disconnectedCallback();
  }

  async updated(changedProperties) {
    super.updated(changedProperties);

    if (changedProperties.has('model')) {
      await this.__fetchHeaderInformation();
      await this.__loadExistingProgressionOfCare();
    }
  }

  async __loadExistingProgressionOfCare() {
    if (!this.model?.patientId) {
      return;
    }

    if (this.__abortController) {
      this.__abortController.abort();
    }

    this.__abortController = new AbortController();

    try {
      const response = await getProgressionOfCare(
        this.model.patientId,
        this.__abortController.signal,
      );

      if (response === undefined || response.length === 0) {
        this.showCards = false;
      } else {
        this.primaryConcerns = formatNarrative(response[0].primaryConcerns);
        this.progressionOfCare = formatNarrative(response[0].progressionOfCare);
        this.providerNotes = formatNarrative(response[0].providerNotes);

        this.formattedHeaderInfo = {
          ...this.formattedHeaderInfo,
          summaryDate: parseDate(response[0].createdAt).format('MM/DD/YYYY'),
        };

        this.showCards = true;
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.showCards = false;
        console.error('Error loading existing progression of care:', error);
      }
    }
  }

  async __fetchHeaderInformation() {
    try {
      const headerFilters = {
        providerIds: this.appliedFilters.providers,
        caseIds: this.appliedFilters.cases,
        dosFrom: this.appliedFilters.dosFrom,
        dosTo: this.appliedFilters.dosTo,
      };

      this.headerLoading = true;

      const headerInfo = await getProgressionOfCareHeader(
        this.model.patientId,
        headerFilters,
      );

      this.headerInfo = headerInfo;
      this.formattedHeaderInfo = formatHeaderInfo(headerInfo);

      this.headerLoading = false;
    } catch (error) {
      this.headerLoading = false;
      store.dispatch(openError(ERROR_MESSAGE_HEADER));
      console.error('Error fetching header information:', error);
    }
  }

  async __fetchFilterData() {
    try {
      const [providers, cases] = await Promise.all([
        getActiveProviderUsers(true),
        getCases(this.model.patientId, true),
      ]);

      const formattedProviders = providers.map(provider => ({
        label: `${provider.firstName} ${provider.lastName}`,
        value: { id: provider.id },
        checked: false,
      }));

      const formattedCases = cases.map(caseItem => ({
        label: `${caseItem.name} - ${caseItem.onsetSymptomsDate ? parseDate(caseItem.onsetSymptomsDate).format('MM/DD/YYYY') : 'Gradual'}`,
        value: { id: caseItem.id },
        checked: false,
      }));

      this.filterItems = {
        providers: formattedProviders,
        cases: formattedCases,
      };
    } catch (error) {
      store.dispatch(openError(ERROR_MESSAGE_FILTER_DATA));
      console.error('Error fetching filter data:', error);
      this.filterItems = {
        providers: [],
        cases: [],
      };
    }
  }

  __getPracticeInformation() {
    const state = store.getState();
    const practiceInformation = state.practiceInformation?.item;

    return {
      name: practiceInformation?.name || '',
      id: practiceInformation?.id || '',
    };
  }

  __getPatientInformation() {
    return {
      firstName: this.headerInfo?.patient?.first || '',
      lastName: this.headerInfo?.patient?.last || '',
      dateOfBirth: this.headerInfo?.patient?.dateOfBirth || '',
    };
  }

  __getVisitInformation() {
    return {
      firstVisit: this.headerInfo?.firstVisit
        ? parseDate(this.headerInfo.firstVisit).format('YYYY-MM-DD')
        : '',
      lastVisit: this.headerInfo?.lastVisit
        ? parseDate(this.headerInfo.lastVisit).format('YYYY-MM-DD')
        : '',
      nextVisit: this.headerInfo?.nextVisit
        ? parseDate(this.headerInfo.nextVisit).format('YYYY-MM-DD')
        : '',
      totalVisits: this.headerInfo
        ? `${this.headerInfo.totalPastVisits} of ${this.headerInfo.totalAppointments}`
        : '',
    };
  }

  __getSummaryAsOfDate() {
    return this.headerInfo?.summaryLastUpdated
      ? parseDate(this.headerInfo.summaryLastUpdated).format('YYYY-MM-DD')
      : '';
  }

  __buildPdfPayload() {
    return {
      practiceInformation: this.__getPracticeInformation(),
      patient: this.__getPatientInformation(),
      summaryAsOf: this.__getSummaryAsOfDate(),
      visitInfo: this.__getVisitInformation(),
      primaryConcerns: this.sectionVisibility[SECTION_LABELS.PRIMARY_CONCERNS]
        ? this.__getCardContent(SECTION_LABELS.PRIMARY_CONCERNS)
        : '',
      progressionOfCare: this.sectionVisibility[
        SECTION_LABELS.PROGRESSION_OF_CARE
      ]
        ? this.__getCardContent(SECTION_LABELS.PROGRESSION_OF_CARE)
        : '',
      providerNotes: this.sectionVisibility[SECTION_LABELS.PROVIDER_NOTES]
        ? this.__getCardContent(SECTION_LABELS.PROVIDER_NOTES)
        : '',
    };
  }

  __generatePdfBuffer() {
    const pdfBody = this.__buildPdfPayload();
    return printProgressionOfCareSummary({
      updatedPdfBody: pdfBody,
      optOutLoadingIndicator: false,
    });
  }

  async __savePdfToPatientDocuments(pdfBuffer) {
    const pdfBlob = new Blob([Uint8Array.from(pdfBuffer.data).buffer], {
      type: 'application/pdf',
    });

    const documentModel = {
      name: `progression-of-care-summary-${new Date().toISOString().split('T')[0]}`,
      uploadDate: moment(),
      date: moment(),
      note: 'AI-generated progression of care summary',
      file: pdfBlob,
      tags: [],
      encounterId: null,
    };

    const result = await postDocument(documentModel, this.model.patientId);

    store.dispatch(
      openSuccess('Progression of care summary saved successfully'),
    );

    return {
      success: true,
      documentId: result.documentId,
      message: 'Progression of care summary saved successfully',
    };
  }

  async __generateProgressionOfCare() {
    if (this.__abortController) {
      this.__abortController.abort();
    }

    this.__abortController = new AbortController();

    this.primaryConcernsLoading = true;
    this.progressionOfCareLoading = true;
    this.providerNotesLoading = true;

    this.primaryConcerns = '';
    this.progressionOfCare = '';
    this.providerNotes = '';

    try {
      const filterBody = {
        providerIds:
          this.appliedFilters.providers.length > 0
            ? this.appliedFilters.providers
            : [],
        caseIds:
          this.appliedFilters.cases.length > 0 ? this.appliedFilters.cases : [],
        dosFrom: this.appliedFilters.dosFrom,
        dosTo: this.appliedFilters.dosTo,
      };

      const response = await generateProgressionOfCare(
        this.model.patientId,
        this.__abortController.signal,
        filterBody,
      );

      if (response.length === 0) {
        this.primaryConcerns = '';
        this.progressionOfCare = '';
        this.providerNotes = '';
      } else {
        this.primaryConcerns = formatNarrative(response[0].primaryConcerns);
        this.progressionOfCare = formatNarrative(response[0].progressionOfCare);
        this.providerNotes = formatNarrative(response[0].providerNotes);

        const summaryLastUpdated = response[0].generatedAt;

        this.headerInfo = {
          ...this.headerInfo,
          summaryLastUpdated,
        };

        this.formattedHeaderInfo = {
          ...this.formattedHeaderInfo,
          summaryDate: parseDate(summaryLastUpdated).format('MM/DD/YYYY'),
        };

        userpilotTrackEvent.generateAiSummary({
          s3FileKey: response[0].s3FileKey,
        });
      }

      this.primaryConcernsLoading = false;
      this.progressionOfCareLoading = false;
      this.providerNotesLoading = false;
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.primaryConcernsLoading = false;
        this.progressionOfCareLoading = false;
        this.providerNotesLoading = false;

        store.dispatch(openError(ERROR_MESSAGE));
      }
    }
  }

  __copyContentToClipboard() {
    try {
      const content = this.__getVisibleContent();
      const plainText = this.__getPlainTextContent();

      const htmlBlob = new Blob([content], { type: 'text/html' });
      const textBlob = new Blob([plainText], { type: 'text/plain' });

      const data = [
        new ClipboardItem({
          [htmlBlob.type]: htmlBlob,
          [textBlob.type]: textBlob,
        }),
      ];

      navigator.clipboard
        .write(data)
        .then(() => {
          this.copySuccess = true;
          setTimeout(() => {
            this.copySuccess = false;
          }, 2000);
        })
        .catch(error => {
          console.error('Failed to copy content:', error);
        });
    } catch (error) {
      console.error('Error preparing content for clipboard:', error);
    }
  }

  __toggleSectionVisibility(sectionLabel) {
    this.sectionVisibility = {
      ...this.sectionVisibility,
      [sectionLabel]: !this.sectionVisibility[sectionLabel],
    };
  }

  __getVisibleContent() {
    try {
      const title = this.showCards ? CHART_NOTE_SUMMARY_TITLE : '';

      let content = `
        <span>
          <span style="font-size: 24px; font-weight: bold;">${title}</span><br><br>
      `;

      Object.entries(this.sectionVisibility).forEach(([label, isVisible]) => {
        if (isVisible) {
          const cardContent = this.__getCardContent(label);
          content += `
            <span>
              <span style="font-size: 18px; font-weight: bold;">${label}</span>${label === SECTION_LABELS.PROGRESSION_OF_CARE ? '<br>' : ''}
              <span>${cardContent}</span>
            </span>
          `;
        }
      });

      content += '</span>';
      return content;
    } catch (error) {
      console.error('Error generating HTML content:', error);
      return '';
    }
  }

  __getPlainTextContent() {
    try {
      const title = this.showCards ? CHART_NOTE_SUMMARY_TITLE : '';

      let content = `${title}\n\n`;

      Object.entries(this.sectionVisibility).forEach(([label, isVisible]) => {
        if (isVisible) {
          const cardContent = this.__getCardContent(label);
          content += `${label}\n${cardContent}\n\n`;
        }
      });

      return content.trim();
    } catch (error) {
      console.error('Error generating plain text content:', error);
      return '';
    }
  }

  __getCardContent(label) {
    try {
      const contentMap = {
        [SECTION_LABELS.PRIMARY_CONCERNS]:
          this.primaryConcerns || 'No Primary Concerns Found',
        [SECTION_LABELS.PROGRESSION_OF_CARE]:
          this.progressionOfCare || 'No Progression of Care Found',
        [SECTION_LABELS.PROVIDER_NOTES]:
          this.providerNotes || 'No Provider Notes Found',
      };

      return contentMap[label] || '';
    } catch (error) {
      console.error(`Error getting content for card ${label}:`, error);
      return '';
    }
  }

  __isLoading() {
    return (
      this.primaryConcernsLoading ||
      this.progressionOfCareLoading ||
      this.providerNotesLoading
    );
  }

  __areAllCardsCollapsed() {
    return Object.values(this.sectionVisibility).every(value => !value);
  }

  __hasNoContent() {
    return (
      !this.primaryConcerns && !this.progressionOfCare && !this.providerNotes
    );
  }

  __shouldDisableButton() {
    return (
      this.__isLoading() ||
      this.__areAllCardsCollapsed() ||
      this.__hasNoContent()
    );
  }

  __shouldShowNoEncountersMessage() {
    return !this.headerInfo?.totalAppointments;
  }

  __hasAppliedFilters() {
    return (
      this.appliedFilters.providers.length > 0 ||
      this.appliedFilters.cases.length > 0 ||
      this.appliedFilters.dosFrom ||
      this.appliedFilters.dosTo
    );
  }

  __renderSlotContent(content) {
    return content ? html`${unsafeHTML(content)}` : '';
  }

  __renderPatientHeader() {
    return this.headerLoading
      ? html`<neb-skeleton
          id="${ELEMENTS.headerSkeleton.id}"
          class="skeleton-loading"
        ></neb-skeleton>`
      : html`
          <neb-progression-of-care-header
            id="${ELEMENTS.patientHeader.id}"
            .model="${this.formattedHeaderInfo}"
            .onClose=${this.handlers.dismiss}
          ></neb-progression-of-care-header>
        `;
  }

  __renderFilters() {
    return this.__hasProgressionOfCareFilter
      ? html`
          <neb-filters-progression-of-care
            id="${ELEMENTS.filters.id}"
            .items="${this.filterItems}"
            .appliedFilters="${this.appliedFilters}"
            .onApply="${this.handlers.applyFilters}"
            .onReset="${this.handlers.resetFilters}"
            .onChange="${this.handlers.changeFilters}"
          ></neb-filters-progression-of-care>
        `
      : '';
  }

  __renderGenerateSummaryButton() {
    const label =
      this.primaryConcerns !== '' ||
      this.progressionOfCare !== '' ||
      this.providerNotes !== ''
        ? 'Regenerate AI Summary'
        : 'Generate AI Summary';

    return this.__isLoading()
      ? ''
      : html`
          <div class="container-button-summary">
            <neb-button
              id="${ELEMENTS.generateSummaryButton.id}"
              label="${label}"
              trailingIcon="ai"
              .onClick=${this.handlers.generateSummary}
            ></neb-button>
          </div>
        `;
  }

  __renderTitle() {
    return this.showCards
      ? html`
          <div class="container-title">
            <neb-icon class="icon" icon="neb:ai"></neb-icon>
            <span id="${ELEMENTS.title.id}" class="title"
              >${CHART_NOTE_SUMMARY_TITLE}</span
            >
          </div>
        `
      : '';
  }

  __renderCards() {
    if (this.showCards) {
      if (this.__shouldShowNoEncountersMessage()) {
        return html`<div
          id="${ELEMENTS.noEncountersMessage.id}"
          class="no-encounters-message-container"
        >
          <neb-text italic
            >No encounters found, please adjust filters.</neb-text
          >
        </div>`;
      }

      return html`
        <div class="container-cards">
          <neb-collapsible-card
            id="${ELEMENTS.primaryConcerns.id}"
            label="${SECTION_LABELS.PRIMARY_CONCERNS}"
            emptyMessage="No Primary Concerns Found"
            ?showVisibility=${this.showSectionVisibility}
            ?expanded=${this.sectionVisibility[SECTION_LABELS.PRIMARY_CONCERNS]}
            ?loading="${this.primaryConcernsLoading}"
            .onToggleVisibility=${this.handlers.togglePrimaryConcerns}
            >${this.__renderSlotContent(
              this.primaryConcerns,
            )}</neb-collapsible-card
          >
          <neb-collapsible-card
            id="${ELEMENTS.progressionOfCare.id}"
            label="${SECTION_LABELS.PROGRESSION_OF_CARE}"
            emptyMessage="No Progression of Care Found"
            ?showVisibility=${this.showSectionVisibility}
            ?expanded=${this.sectionVisibility[
              SECTION_LABELS.PROGRESSION_OF_CARE
            ]}
            ?loading="${this.progressionOfCareLoading}"
            .onToggleVisibility=${this.handlers.toggleProgressionOfCare}
            >${this.__renderSlotContent(
              this.progressionOfCare,
            )}</neb-collapsible-card
          >
          <neb-collapsible-card
            id="${ELEMENTS.providerNotes.id}"
            label="${SECTION_LABELS.PROVIDER_NOTES}"
            emptyMessage="No Provider Notes Found"
            ?showVisibility=${this.showSectionVisibility}
            ?expanded=${this.sectionVisibility[SECTION_LABELS.PROVIDER_NOTES]}
            ?loading="${this.providerNotesLoading}"
            .onToggleVisibility=${this.handlers.toggleProviderNotes}
            >${this.__renderSlotContent(
              this.providerNotes,
            )}</neb-collapsible-card
          >
        </div>
      `;
    }

    return '';
  }

  __renderActionButtons() {
    if (this.showCards) {
      if (this.__shouldShowNoEncountersMessage()) {
        return '';
      }

      return html`
        <div class="container-buttons">
          <div class="copy-button-wrapper">
            <neb-button
              id="${ELEMENTS.copyButton.id}"
              class="copy-button"
              label="Copy"
              trailingIcon="copy"
              .onClick=${this.handlers.copyContent}
              ?disabled=${this.__shouldDisableButton()}
            ></neb-button>
            <span
              id="${ELEMENTS.copySuccess.id}"
              class="copy-success ${this.copySuccess ? 'visible' : ''}"
            >
              Copied to clipboard!
            </span>
          </div>
          <div class="save-button-wrapper">
            <neb-button
              id="${ELEMENTS.saveButton.id}"
              label="Save"
              trailingIcon="save"
              .onClick=${this.handlers.saveSummary}
              ?disabled=${this.__shouldDisableButton() || this.saveLoading}
            ></neb-button>
            <span
              id="${ELEMENTS.saveSuccess.id}"
              class="save-success ${this.saveSuccess ? 'visible' : ''}"
            >
              Saved to patient documents!
            </span>
          </div>
          <neb-button
            id="${ELEMENTS.printButton.id}"
            label="Print"
            trailingIcon="print"
            .onClick=${this.handlers.printSummary}
            ?disabled=${this.__shouldDisableButton()}
          ></neb-button>
        </div>
      `;
    }
    return '';
  }

  renderContent() {
    return html`
      ${this.__renderPatientHeader()} ${this.__renderFilters()}
      ${this.__renderGenerateSummaryButton()} ${this.__renderTitle()}
      ${this.__renderCards()} ${this.__renderActionButtons()}
    `;
  }
}

window.customElements.define(
  'neb-overlay-progression-of-care',
  NebOverlayProgressionOfCare,
);
